import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { createWallet, getWalletBalance, getDepositAddress, initiateWithdrawal } from '../services/walletService';
import DashboardLayout from '../components/DashboardLayout';

// Styled components
const WalletContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  padding: 2rem;
  color: ${props => props.theme.colors.text};
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Title = styled.h1`
  font-size: 1.8rem;
  margin-bottom: 2rem;
  color: ${props => props.theme.colors.primary};
  border-bottom: 1px solid ${props => props.theme.colors.primary};
  padding-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.4rem;
    margin-bottom: 1rem;
  }
`;

const Section = styled.div`
  background-color: #0a0a0a;
  border-radius: 4px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
    margin-bottom: 1rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.2rem;
  margin-bottom: 1rem;
  color: ${props => props.theme.colors.primary};
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #222;
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`;

const Label = styled.span`
  opacity: 0.7;
`;

const Value = styled.span`
  font-family: monospace;
  background-color: #161616;
  padding: 0.3rem 0.5rem;
  border-radius: 2px;
  max-width: 70%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    max-width: 100%;
    width: 100%;
    font-size: 0.85rem;
  }
`;

const Button = styled.button`
  background-color: ${props => props.theme.colors.primary};
  color: #000;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.theme.colors.accent};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ActionButton = styled(Button)`
  margin-left: 1rem;
  
  @media (max-width: 768px) {
    margin-left: 0.5rem;
    padding: 0.4rem 0.8rem;
    font-size: 0.9rem;
  }
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.colors.error};
  margin-top: 0.5rem;
  background-color: rgba(255, 85, 85, 0.1);
  padding: 0.5rem;
  border-radius: 4px;
`;

const Success = styled.div`
  color: ${props => props.theme.colors.success};
  margin-top: 0.5rem;
  background-color: rgba(85, 255, 127, 0.1);
  padding: 0.5rem;
  border-radius: 4px;
`;

const InputRow = styled.div`
  display: flex;
  margin-bottom: 1rem;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`;

const Input = styled.input`
  background-color: #161616;
  border: 1px solid #333;
  color: ${props => props.theme.colors.text};
  padding: 0.5rem;
  border-radius: 4px;
  margin-right: 1rem;
  font-family: monospace;
  flex: 1;
  
  &:focus {
    border-color: ${props => props.theme.colors.primary};
    outline: none;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    margin-right: 0;
  }
`;

const CopyButton = styled.button`
  background-color: transparent;
  border: none;
  color: ${props => props.theme.colors.primary};
  cursor: pointer;
  font-size: 0.8rem;
  padding: 0.2rem 0.5rem;
  
  &:hover {
    text-decoration: underline;
  }
`;

const LoadingIndicator = styled.div`
  color: ${props => props.theme.colors.primary};
  margin-left: 1rem;
`;

const WalletPage: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  const [isCreatingWallet, setIsCreatingWallet] = useState(false);
  const [walletBalance, setWalletBalance] = useState<number | null>(null);
  const [isLoadingBalance, setIsLoadingBalance] = useState(false);
  const [depositAddress, setDepositAddress] = useState<string | null>(null);
  const [isGettingDepositAddress, setIsGettingDepositAddress] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawAddress, setWithdrawAddress] = useState('');
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
    } else {
      setIsLoading(false);
    }
  }, [isAuthenticated, navigate]);

  // Get wallet balance
  const fetchBalance = async () => {
    if (!user?.embeddedWalletAddress) return;
    
    try {
      setIsLoadingBalance(true);
      setError(null);
      const balanceData = await getWalletBalance(user.embeddedWalletAddress);
      setWalletBalance(balanceData.balance);
    } catch (error) {
      console.error('Error fetching balance:', error);
      setError('Failed to fetch wallet balance');
    } finally {
      setIsLoadingBalance(false);
    }
  };

  // Create wallet
  const handleCreateWallet = async () => {
    if (!user?.privyUserId) return;
    
    try {
      setIsCreatingWallet(true);
      setError(null);
      setSuccess(null);
      
      const result = await createWallet(user.privyUserId);
      
      if (result.success) {
        setSuccess('Wallet created successfully!');
        // Update local state instead of reloading
        if (result.walletAddress) {
          // Update the user's wallet address in the local state
          user.embeddedWalletAddress = result.walletAddress;
          // Fetch the initial balance
          fetchBalance();
        }
      }
    } catch (error) {
      console.error('Error creating wallet:', error);
      setError('Failed to create wallet. Please try again.');
    } finally {
      setIsCreatingWallet(false);
    }
  };

  // Get deposit address
  const handleGetDepositAddress = async () => {
    if (!user?.privyUserId) return;
    
    try {
      setIsGettingDepositAddress(true);
      setError(null);
      
      const result = await getDepositAddress(user.privyUserId);
      setDepositAddress(result.depositAddress);
    } catch (error) {
      console.error('Error getting deposit address:', error);
      setError('Failed to get deposit address');
    } finally {
      setIsGettingDepositAddress(false);
    }
  };

  // Initiate withdrawal
  const handleWithdrawal = async () => {
    if (!user?.privyUserId || !withdrawAddress || !withdrawAmount) {
      setError('Please enter a valid address and amount');
      return;
    }
    
    try {
      setIsWithdrawing(true);
      setError(null);
      setSuccess(null);
      
      const amount = parseFloat(withdrawAmount);
      if (isNaN(amount) || amount <= 0) {
        setError('Please enter a valid amount');
        setIsWithdrawing(false);
        return;
      }
      
      const result = await initiateWithdrawal(user.privyUserId, withdrawAddress, amount);
      
      if (result.success) {
        setSuccess('Withdrawal initiated successfully!');
        setWithdrawAmount('');
        setWithdrawAddress('');
        // Refresh balance after a short delay
        setTimeout(fetchBalance, 2000);
      }
    } catch (error) {
      console.error('Error initiating withdrawal:', error);
      setError('Failed to initiate withdrawal');
    } finally {
      setIsWithdrawing(false);
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSuccess('Copied to clipboard!');
    setTimeout(() => setSuccess(null), 2000);
  };

  // Fetch balance when component mounts if user has a wallet
  useEffect(() => {
    if (user?.embeddedWalletAddress) {
      fetchBalance();
    }
  }, [user?.embeddedWalletAddress]);

  // Loading state
  if (isLoading) {
    return (
      <DashboardLayout activeMenuItem="wallet">
        <WalletContainer>
          <Title>Wallet</Title>
          <div>Loading wallet information...</div>
        </WalletContainer>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout activeMenuItem="wallet">
      <WalletContainer>
        <Title>Wallet Management</Title>
        
        <Section>
          <SectionTitle>Wallet Status</SectionTitle>
          
          {user?.embeddedWalletAddress ? (
            <>
              <InfoRow>
                <Label>Wallet Address</Label>
                <div>
                  <Value title={user.embeddedWalletAddress}>
                    {user.embeddedWalletAddress}
                  </Value>
                  <CopyButton onClick={() => copyToClipboard(user.embeddedWalletAddress!)}>
                    Copy
                  </CopyButton>
                </div>
              </InfoRow>
              <InfoRow>
                <Label>Balance</Label>
                <div>
                  {isLoadingBalance ? (
                    <LoadingIndicator>Loading...</LoadingIndicator>
                  ) : (
                    <>
                      <Value>{walletBalance !== null ? `${walletBalance} SOL` : 'Unknown'}</Value>
                      <ActionButton onClick={fetchBalance} disabled={isLoadingBalance}>
                        Refresh
                      </ActionButton>
                    </>
                  )}
                </div>
              </InfoRow>
            </>
          ) : (
            <div>
              <p>You don't have a wallet yet. Create one to start trading!</p>
              <Button 
                onClick={handleCreateWallet} 
                disabled={isCreatingWallet}
              >
                {isCreatingWallet ? 'Creating...' : 'Create Wallet'}
              </Button>
            </div>
          )}
          
          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <Success>{success}</Success>}
        </Section>
        
        {user?.embeddedWalletAddress && (
          <>
            <Section>
              <SectionTitle>Deposit</SectionTitle>
              {depositAddress ? (
                <>
                  <p>Send funds to this address:</p>
                  <InfoRow>
                    <Value title={depositAddress}>
                      {depositAddress}
                    </Value>
                    <CopyButton onClick={() => copyToClipboard(depositAddress)}>
                      Copy
                    </CopyButton>
                  </InfoRow>
                </>
              ) : (
                <Button 
                  onClick={handleGetDepositAddress} 
                  disabled={isGettingDepositAddress}
                >
                  {isGettingDepositAddress ? 'Getting address...' : 'Get Deposit Address'}
                </Button>
              )}
            </Section>
            
            <Section>
              <SectionTitle>Withdraw</SectionTitle>
              <InputRow>
                <Label>Address:</Label>
                <Input 
                  type="text" 
                  value={withdrawAddress} 
                  onChange={(e) => setWithdrawAddress(e.target.value)} 
                  placeholder="Enter destination address"
                />
              </InputRow>
              <InputRow>
                <Label>Amount:</Label>
                <Input 
                  type="number" 
                  placeholder="0.00" 
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                />
                <Button 
                  onClick={handleWithdrawal} 
                  disabled={isWithdrawing || !withdrawAddress || !withdrawAmount}
                >
                  {isWithdrawing ? 'Processing...' : 'Withdraw'}
                </Button>
              </InputRow>
            </Section>
          </>
        )}
      </WalletContainer>
    </DashboardLayout>
  );
};

export default WalletPage; 